/** Learn hub home (/academy): hero and search, featured, popular, platform guide, learning paths, latest, topics, categories. */
import { useState, type FormEvent } from 'react';
import { BRAND } from '@platform/shared';
import { cx, Icon } from '../../../components/ui';
import { Link, navigate } from '../../../lib/router';
import { courseArticles, formatDate, sortNewest, sortPopular, topicCounts } from './catalog';
import { ARTICLES, articleBySlug } from './content';
import { useReadSet } from './progress';
import { learnPath, TRACKS_PATH } from './routes';
import { CATEGORIES, COURSES, courseInfo, FEATURED_TOPICS, GUIDE_TABS, topicInfo, type CourseInfo } from './taxonomy';
import type { ArticleMeta } from './types';
import { ArticleCard, CardGrid, CATEGORY_ICON, COURSE_ICON, Cover, IconBadge, LEVEL_BAR, LevelBadge, ProgressBar, SectionHead } from './ui';

function HeroArt() {
  // Original illustration: a price card with candles and a trend line, with coin, gold and shield tokens around it.
  const candles = [
    [40, 150, 120, 1],
    [70, 135, 112, 1],
    [100, 120, 128, 0],
    [130, 128, 96, 1],
    [160, 104, 84, 1],
    [190, 90, 100, 0],
    [220, 96, 62, 1],
    [250, 70, 50, 1],
  ] as const;
  return (
    <svg viewBox="0 0 440 340" role="img" aria-label="Illustration of a rising price chart with crypto, gold and USDT tokens" className="h-auto w-full max-w-[460px]">
      <defs>
        <linearGradient id="ha-card" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--card)" />
          <stop offset="1" stopColor="var(--panel)" />
        </linearGradient>
        <linearGradient id="ha-glow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3d5cf5" stopOpacity="0.55" />
          <stop offset="1" stopColor="#b58cff" stopOpacity="0.15" />
        </linearGradient>
        <linearGradient id="ha-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3d5cf5" stopOpacity="0.28" />
          <stop offset="1" stopColor="#3d5cf5" stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle cx="250" cy="160" r="150" fill="url(#ha-glow)" opacity="0.5" />
      <g transform="translate(70 60)">
        <rect width="300" height="210" rx="22" fill="url(#ha-card)" stroke="var(--border-strong)" />
        <rect x="20" y="18" width="70" height="9" rx="4.5" fill="var(--border-strong)" />
        <rect x="20" y="34" width="110" height="14" rx="7" fill="var(--text)" opacity="0.85" />
        <rect x="222" y="20" width="58" height="22" rx="11" fill="#1fc7a0" opacity="0.18" />
        <rect x="232" y="28" width="38" height="6" rx="3" fill="#1fc7a0" />
        {[80, 115, 150, 185].map((y) => (
          <line key={y} x1="20" x2="280" y1={y} y2={y} stroke="var(--border)" strokeDasharray="3 5" />
        ))}
        <path d="M20 168 L60 150 L100 156 L130 132 L160 118 L190 124 L220 92 L250 74 L280 60 L280 196 L20 196 Z" fill="url(#ha-area)" />
        {candles.map(([x, open, close, up], i) => {
          const top = Math.min(open, close);
          const h = Math.max(4, Math.abs(open - close));
          const c = up ? '#1fc7a0' : '#f2557a';
          return (
            <g key={i}>
              <line x1={x} x2={x} y1={top - 10} y2={top + h + 10} stroke={c} strokeWidth="2" strokeLinecap="round" />
              <rect x={x - 7} y={top} width="14" height={h} rx="3" fill={c} />
            </g>
          );
        })}
      </g>
      <g transform="translate(36 214)">
        <circle r="34" fill="#26a17b" />
        <circle r="26" fill="none" stroke="#fff" strokeOpacity="0.35" strokeWidth="2" />
        <path d="M-14 -12 H14 M0 -12 V16 M-11 -1 C-6 3 6 3 11 -1" stroke="#fff" strokeWidth="5" strokeLinecap="round" fill="none" />
      </g>
      <g transform="translate(392 70)">
        <circle r="30" fill="#f7931a" />
        <text y="11" textAnchor="middle" fontSize="32" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">
          ₿
        </text>
      </g>
      <g transform="translate(330 280) rotate(-8)">
        <path d="M-40 14 L-28 -14 H28 L40 14 Z" fill="#e8b84a" />
        <path d="M-28 -14 H28 L22 -4 H-22 Z" fill="#f6d27a" />
      </g>
      <g transform="translate(118 36)">
        <path d="M0 -22 L18 -14 V2 C18 14 9 22 0 26 C-9 22 -18 14 -18 2 V-14 Z" fill="#3d5cf5" />
        <path d="M-7 2 L-2 7 L8 -4" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>
    </svg>
  );
}

function Hero() {
  const [q, setQ] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(learnPath.articles({ q: q.trim() }));
  };
  return (
    <section className="relative mt-6 grid items-center gap-6 overflow-hidden rounded-[28px] border border-line bg-[radial-gradient(120%_120%_at_0%_0%,var(--accent-bg),transparent_55%),radial-gradient(90%_90%_at_100%_100%,var(--managed-bg),transparent_60%)] px-5 py-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:px-12 md:py-14">
      <div className="flex min-w-0 flex-col gap-5">
        <span className="inline-flex items-center gap-2 self-start rounded-full bg-card px-3 py-1 text-[12.5px] font-semibold text-accent-fg shadow-[0_1px_2px_var(--shadow)]">
          <Icon name="star" size={13} /> {ARTICLES.length} free lessons · Beginner to Advanced
        </span>
        <h1 className="m-0 text-[clamp(32px,5vw,54px)] leading-[1.05] font-semibold tracking-tight text-balance">Learn to trade with confidence, one lesson at a time</h1>
        <p className="m-0 max-w-[520px] text-[17px] leading-relaxed text-muted">
          Plain-English guides to crypto, commodities and stock CFDs, from your first order to advanced risk management. Every example is worked through in USDT and shown on {BRAND.name}.
        </p>
        <form role="search" onSubmit={submit} className="flex w-full max-w-[520px] items-center gap-2 rounded-full border border-line-strong bg-card p-1.5 pl-5 shadow-[0_10px_30px_var(--shadow)]">
          <Icon name="search" size={17} className="shrink-0 text-muted" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search: leverage, stop loss, RSI…"
            aria-label="Search the Academy"
            className="h-11 min-w-0 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-muted"
          />
          <button type="submit" className="h-11 shrink-0 rounded-full bg-accent px-5 text-[14.5px] font-semibold text-white">
            Search
          </button>
        </form>
        <div className="flex flex-wrap gap-2 text-[13px]">
          {['Leverage', 'Stop loss', 'Candlesticks', 'Position size'].map((t) => (
            <Link key={t} to={learnPath.articles({ q: t.toLowerCase() })} className="rounded-full bg-card px-3 py-1 font-medium text-muted no-underline hover:text-fg">
              {t}
            </Link>
          ))}
        </div>
      </div>
      <div className="flex justify-center md:justify-end">
        <HeroArt />
      </div>
    </section>
  );
}

function Featured({ article }: { article: ArticleMeta }) {
  const course = courseInfo(article.course)!;
  return (
    <Link
      to={learnPath.article(article.slug)}
      className="group grid overflow-hidden rounded-[22px] border border-line bg-card text-fg no-underline transition-shadow hover:shadow-[0_18px_44px_var(--shadow)] md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]"
    >
      <Cover article={article} eager className="rounded-none" />
      <div className="flex flex-col justify-center gap-4 p-6 md:p-10">
        <div className="flex flex-wrap items-center gap-2.5 text-[13px] text-muted">
          <span className="font-mono text-[11px] font-semibold tracking-[0.1em] text-accent-fg uppercase">Featured</span>
          <LevelBadge level={article.level} />
          <span>{course.title}</span>
        </div>
        <h2 className="m-0 text-[26px] leading-tight font-semibold tracking-tight group-hover:text-accent-fg md:text-[32px]">{article.title}</h2>
        <p className="m-0 line-clamp-3 text-[15.5px] leading-relaxed text-muted">{article.description}</p>
        <div className="flex flex-wrap items-center gap-4 text-[13.5px] text-muted">
          <span className="inline-flex h-11 items-center gap-2 rounded-full bg-fg px-5 text-[14px] font-semibold text-bg">
            Read the guide <Icon name="right" size={15} />
          </span>
          <span>
            {article.readTime} min read · {formatDate(article.publishedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function PlatformGuide({ read }: { read: ReadonlySet<string> }) {
  const [tab, setTab] = useState(GUIDE_TABS[0]!.id);
  const current = GUIDE_TABS.find((t) => t.id === tab)!;
  const list = current.slugs.map(articleBySlug).filter((a): a is ArticleMeta => !!a);
  return (
    <section className="flex flex-col gap-6 rounded-[26px] bg-panel p-5 md:p-8">
      <SectionHead title="Platform Guide" sub={`How to do it on ${BRAND.name}, screen by screen.`} to={learnPath.course('platform-guide')} more="Explore courses" icon={COURSE_ICON['platform-guide']} />
      <div role="tablist" aria-label="Platform Guide" className="-mx-1 flex gap-1.5 overflow-x-auto px-1 no-scrollbar">
        {GUIDE_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`guide-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls="guide-panel"
            onClick={() => setTab(t.id)}
            className={cx('h-10 shrink-0 rounded-full px-4 text-[14px] font-semibold', tab === t.id ? 'bg-fg text-bg' : 'bg-card text-fg hover:bg-pill')}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div id="guide-panel" role="tabpanel" aria-labelledby={`guide-tab-${tab}`}>
        <CardGrid>
          {list.map((a) => (
            <ArticleCard key={a.slug} article={a} read={read.has(a.slug)} />
          ))}
        </CardGrid>
      </div>
    </section>
  );
}

function PathCard({ course, read }: { course: CourseInfo; read: ReadonlySet<string> }) {
  const lessons = courseArticles(ARTICLES, course.id);
  const done = lessons.filter((l) => read.has(l.slug)).length;
  const next = lessons.find((l) => !read.has(l.slug)) ?? lessons[0];
  const minutes = lessons.reduce((t, l) => t + l.readTime, 0);
  const level = course.level ?? 'beginner';
  const action = done === 0 ? 'Start the path' : done === lessons.length ? 'Review' : 'Continue';
  return (
    <div className="relative flex flex-col gap-5 overflow-hidden rounded-[22px] border border-line bg-card p-6">
      <span aria-hidden="true" className={cx('absolute inset-x-0 top-0 h-1.5', LEVEL_BAR[level])} />
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-3">
          <IconBadge icon={COURSE_ICON[course.id]} size="md" />
          <LevelBadge level={level} />
        </span>
        <span className="text-[13px] text-muted">
          {lessons.length} lessons · {Math.round(minutes / 6) / 10} h
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <Link to={learnPath.course(course.id)} className="text-[22px] font-semibold tracking-tight text-fg no-underline hover:text-accent-fg">
          {course.title}
        </Link>
        <p className="m-0 text-[14.5px] leading-relaxed text-muted">{course.intro}</p>
      </div>
      <div className="mt-auto flex flex-col gap-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-muted">Your progress</span>
          <span className="num font-semibold">
            {done}/{lessons.length}
          </span>
        </div>
        <ProgressBar done={done} total={lessons.length} tone={LEVEL_BAR[level]} label={`${course.title}: ${done} of ${lessons.length} lessons read`} />
      </div>
      {next && (
        <Link to={learnPath.article(next.slug)} className="flex items-center justify-between gap-3 rounded-[14px] bg-panel px-4 py-3 text-fg no-underline hover:bg-pill">
          <span className="flex min-w-0 flex-col">
            <span className="text-[12px] font-semibold tracking-[0.06em] text-muted uppercase">{action}</span>
            <span className="truncate text-[14.5px] font-semibold">
              {next.order}. {next.title}
            </span>
          </span>
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-fg text-bg">
            <Icon name="right" size={16} />
          </span>
        </Link>
      )}
    </div>
  );
}

const TOPIC_TILE = ['from-[#3d5cf5] to-[#1b2a7a]', 'from-[#0f8f74] to-[#0b3d36]', 'from-[#7340d6] to-[#2a1458]'];

function TopicTiles() {
  const counts = topicCounts(ARTICLES);
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {FEATURED_TOPICS.map((id, i) => {
        const t = topicInfo(id)!;
        return (
          <Link key={id} to={learnPath.topic(id)} className={cx('group relative flex min-h-[220px] flex-col justify-end gap-2 overflow-hidden rounded-[22px] bg-gradient-to-br p-6 text-white no-underline', TOPIC_TILE[i % 3])}>
            <svg aria-hidden="true" viewBox="0 0 200 120" className="absolute -top-2 -right-4 w-[62%] opacity-30 transition-transform duration-300 group-hover:scale-105">
              <path d="M0 100 L30 80 L60 88 L90 52 L120 60 L150 28 L200 12" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              {[30, 60, 90, 120, 150].map((x, k) => (
                <rect key={x} x={x - 5} y={40 + ((k * 23) % 40)} width="10" height={22 + ((k * 11) % 18)} rx="2" fill="#fff" />
              ))}
            </svg>
            <span className="relative font-mono text-[11.5px] tracking-[0.1em] text-white/70 uppercase">{counts.get(id) ?? 0} articles</span>
            <span className="relative text-[24px] font-semibold tracking-tight">{t.title}</span>
            <span className="relative max-w-[320px] text-[14.5px] leading-relaxed text-white/80">{t.blurb}</span>
            <span className="relative mt-1 inline-flex items-center gap-1 text-[14px] font-semibold">
              Explore <Icon name="right" size={14} />
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/** The tracks' hero render (scripts/academy-art); the path is written out so the hub doesn't load the tracks' lesson data. */
const TRACKS_IMAGE = '/academy/hero.webp';

function TracksPromo() {
  return (
    <section className="relative overflow-hidden rounded-[24px] bg-hero text-hero-fg">
      <img src={TRACKS_IMAGE} alt="" loading="lazy" decoding="async" className="absolute inset-0 size-full object-cover object-right opacity-70" />
      <span aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.92)_0%,rgba(0,0,0,0.7)_50%,rgba(0,0,0,0.1)_100%)]" />
      <div className="relative flex max-w-[560px] flex-col gap-3 p-6 md:p-10">
        <span className="font-mono text-[11px] tracking-[0.12em] text-hero-muted uppercase">Learn by doing</span>
        <h2 className="m-0 text-[26px] leading-tight font-semibold">Interactive tracks with quick checks and demo practice</h2>
        <p className="m-0 text-[15px] leading-relaxed text-hero-muted">Five-minute lessons on each screen of the platform. Sign in to save your progress and complete Earn quests as you go.</p>
        <Link to={TRACKS_PATH} className="mt-2 inline-flex h-11 items-center gap-2 self-start rounded-full bg-white px-5 text-[14px] font-semibold text-black no-underline">
          Open the tracks <Icon name="right" size={15} />
        </Link>
      </div>
    </section>
  );
}

export function Home() {
  const read = useReadSet();
  const featured = ARTICLES.find((a) => a.featured) ?? sortPopular(ARTICLES)[0];
  const popular = sortPopular(ARTICLES.filter((a) => a !== featured)).slice(0, 4);
  const latest = sortNewest(ARTICLES).slice(0, 4);
  // Category rows show articles not already on the page above them, topped up if a category runs short.
  const shown = new Set([featured, ...popular, ...latest].map((a) => a?.slug));
  const categoryRow = (id: string) => {
    const all = sortNewest(ARTICLES.filter((a) => a.category === id));
    return [...all.filter((a) => !shown.has(a.slug)), ...all.filter((a) => shown.has(a.slug))].slice(0, 4);
  };
  return (
    <div className="flex flex-col gap-16 pb-4 md:gap-20">
      <Hero />
      {featured && <Featured article={featured} />}
      <section className="flex flex-col gap-6">
        <SectionHead title="Popular" sub="Where most traders start." to={learnPath.articles({ sort: 'popular' })} more="All posts" />
        <CardGrid>
          {popular.map((a) => (
            <ArticleCard key={a.slug} article={a} read={read.has(a.slug)} />
          ))}
        </CardGrid>
      </section>
      <PlatformGuide read={read} />
      <section className="flex flex-col gap-6">
        <SectionHead title="Learning paths" sub="Follow a path in order. Progress is saved in this browser." />
        <div className="grid gap-5 lg:grid-cols-3">
          {COURSES.filter((c) => c.level).map((c) => (
            <PathCard key={c.id} course={c} read={read} />
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-6">
        <SectionHead title="Latest posts" to={learnPath.articles()} more="All posts" />
        <CardGrid>
          {latest.map((a) => (
            <ArticleCard key={a.slug} article={a} read={read.has(a.slug)} />
          ))}
        </CardGrid>
      </section>
      <section className="flex flex-col gap-6">
        <SectionHead title="Featured topics" to={learnPath.articles()} more="All topics" />
        <TopicTiles />
      </section>
      {CATEGORIES.map((c) => {
        const list = categoryRow(c.id);
        if (!list.length) return null;
        return (
          <section key={c.id} className="flex flex-col gap-6">
            <SectionHead title={c.title} sub={c.blurb} to={learnPath.category(c.id)} icon={CATEGORY_ICON[c.id]} />
            <CardGrid>
              {list.map((a) => (
                <ArticleCard key={a.slug} article={a} read={read.has(a.slug)} />
              ))}
            </CardGrid>
          </section>
        );
      })}
      <TracksPromo />
    </div>
  );
}
